// src/admin/components/sidebar/Sidebar.jsx - Apartment Management System
import { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  RiDashboardLine, RiShieldUserLine, RiUserLine, RiHomeHeartLine,
  RiTeamLine, RiMoneyDollarCircleLine, RiFileChartLine, RiSettings4Line,
  RiLogoutBoxLine, RiBuildingLine, RiArrowDownSLine, RiArrowRightSLine,
  RiArrowLeftSLine, RiMenu4Line, RiAddLine, RiFileList3Line, RiUserStarLine,
  RiArchiveLine, RiCalendarEventLine, RiUserHeartLine, RiFileUserLine,
  RiPieChartLine, RiBarChartBoxLine, RiLineChartLine, RiUserSettingsLine,
  RiAdminLine, RiCommunityLine, RiWallet3Line, RiFileTextLine, RiLinksLine,
  RiHistoryLine, RiReceiptLine, RiSecurePaymentLine, RiFileHistoryLine, RiServiceLine, RiWifiLine,
  RiWhatsappLine, RiCalendarCheckLine, RiCalendarLine, RiOrganizationChart,
  RiShieldCheckLine, RiClipboardLine, RiFileList2Line, RiFileListLine, RiSettingsLine, RiLockLine, RiMessage3Line, RiToolsLine, RiMegaphoneLine, RiSmartphoneLine,
  RiAlarmWarningLine, RiBuilding4Line, RiBellLine, RiCarLine, RiUpload2Line,
  RiAlertLine, RiMailLine, RiSendPlaneLine,
  RiBrainLine, RiFlashlightLine, RiCalendarTodoLine, RiSurveyLine } from
'react-icons/ri';
import api from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllBuildings } from '../../../models/buildingModel';
import {
  isAdmin as isAdminRole,
  canOperateSystem,
  canManageLeases,
  canManageLeaseApplications,
  canManageFinancials,
  canManagePayments,
  canViewReports,
  canViewBasicReports,
  canViewFinancialReports,
  canManageMaintenance,
  canManageSettings,
  canManageOwners,
  canManageProperties,
  canManageScreening,
  canManageFinancialData,
  canManageMaterials,
  isManagement,
  formatRoleName,
  ROLES } from
'../../../utils/roles';

const Sidebar = ({ isOpen, setIsOpen, isMobile }) => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const { user, logout, hasFeature, setIsSubscriptionModalOpen } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({});
  const [firstBuildingId, setFirstBuildingId] = useState(null);
  const [orgName, setOrgName] = useState('');

  const isAdmin = isAdminRole(user?.role);
  const isPropertyManager = canManageProperties(user?.role);
  const hasFinancials = canManageFinancials(user?.role);
  const hasFinancialData = canManageFinancialData(user?.role);
  const hasMaterials = canManageMaterials(user?.role);
  const hasPayments = canManagePayments(user?.role);
  const hasReports = canViewReports(user?.role);
  const hasBasicReports = canViewBasicReports(user?.role);
  const hasFinancialReports = canViewFinancialReports(user?.role);
  const hasLeasing = canManageLeases(user?.role);
  const hasLeaseApps = canManageLeaseApplications(user?.role);
  const hasMaintenance = canManageMaintenance(user?.role);
  const hasSettings = canManageSettings(user?.role);
  const hasOwners = canManageOwners(user?.role);
  const hasScreening = canManageScreening(user?.role);
  const isSeniorManagement = isManagement(user?.role);
  const isVendor = user?.role === ROLES.VENDOR;
  const isLeasingAgent = user?.role === ROLES.LEASING_AGENT;

  // Only lock AI features in the sidebar — other feature gates are enforced at the page level
  const isItemLocked = (item) => {
    if (item.feature === 'ai_features') {
      return !hasFeature('ai_features');
    }
    return false;
  };

  // Initialize expanded state based on current location
  useEffect(() => {
    const path = location.pathname;
    const menuState = {};

    menuItems.forEach((section) => {
      section.items.forEach((item) => {
        if (item.subItems) {
          const isActive = item.path === '/admin/ai' ?
          aiChildPaths.some((p) => path.startsWith(p)) :
          path.startsWith(item.path);
          if (isActive) menuState[item.path] = true;
        }
      });
    });

    setExpandedMenus(menuState);
  }, [location.pathname]);

  // Fetch first available building (only for non-vendor roles)
  useEffect(() => {
    const fetchFirstBuilding = async () => {
      try {
        const buildingsData = await getAllBuildings();

        // Handle various response formats: direct array, { buildings: [...] }, or { data: [...] }
        let buildings = [];
        if (Array.isArray(buildingsData)) {
          buildings = buildingsData;
        } else if (buildingsData?.buildings && Array.isArray(buildingsData.buildings)) {
          buildings = buildingsData.buildings;
        } else if (buildingsData?.data && Array.isArray(buildingsData.data)) {
          buildings = buildingsData.data;
        }

        if (buildings && buildings.length > 0) {
          const id = buildings[0].id || buildings[0]._id;
          setFirstBuildingId(id);
        }
      } catch (error) {
        console.error('Error fetching first building for sidebar:', error);
      }
    };

    if (!isVendor) {
      fetchFirstBuilding();
    }
  }, [user]);

  // Fetch organization name for ALL roles (backend allows all roles access to GET /settings)
  useEffect(() => {
    if (!user) return;
    const fetchOrgSettings = async () => {
      try {
        const response = await api.get('/organization/settings');
        if (response.data.success && response.data.organization) {
          setOrgName(response.data.organization.name);
        }
      } catch (error) {
        console.error('Error fetching organization name for sidebar:', error);
      }
    };
    fetchOrgSettings();
  }, [user]);

  // Check if a route is active, including nested routes
  const aiChildPaths = ['/admin/ai-pricing', '/admin/churn', '/admin/showings', '/admin/surveys', '/admin/insurance'];
  const accountingChildPaths = ['/admin/accounting', '/admin/expenses', '/admin/gl', '/admin/financials/forecast'];
  const paymentsChildPaths = ['/admin/payments', '/admin/services', '/admin/late-fees', '/admin/utilities', '/admin/security-deposits', '/admin/owner-payout', '/admin/owners/financials'];
  const maintenanceChildPaths = ['/admin/maintenance', '/admin/work-orders', '/admin/storage', '/admin/vendors'];
  const isRouteActive = (path) => {
    if (path === '/admin/ai') {
      return aiChildPaths.some((p) => location.pathname.startsWith(p));
    }
    if (path === '/admin/expenses') {
      return accountingChildPaths.some((p) => location.pathname.startsWith(p));
    }
    if (path === '/admin/payments') {
      return paymentsChildPaths.some((p) => location.pathname.startsWith(p));
    }
    if (path === '/admin/maintenance') {
      return maintenanceChildPaths.some((p) => location.pathname.startsWith(p));
    }
    return location.pathname.startsWith(path);
  };

  // Toggle submenu
  const toggleSubmenu = (path) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu items with organized sections
  const menuItems = [
  // Core Section
  {
    section: 'core',
    items: [
    {
      path: '/admin/dashboard',
      name: t('dashboard'),
      icon: <RiDashboardLine size={20} />,
      exact: true
    }]

  },

  // User Management Section (Admin Only)
  ...(isAdmin ? [{
    section: 'user_management',
    items: [
    {
      path: '/admin/users',
      name: t('system_users'),
      icon: <RiShieldUserLine size={20} />,
      subItems: [
      {
        path: '/admin/users',
        name: t('user_list'),
        icon: <RiUserStarLine size={18} />
      },
      {
        path: '/admin/users/add',
        name: t('add_user'),
        icon: <RiAddLine size={18} />
      }]

    }]

  }] : []),

  // Property Management Section
  {
    section: 'property_management',
    items: [
    {
      path: '/admin/buildings',
      name: t('buildings'),
      icon: <RiBuildingLine size={20} />,
      subItems: [
      {
        path: '/admin/buildings',
        name: t('building_list'),
        icon: <RiBuildingLine size={18} />
      },
      ...(isPropertyManager ? [{
        path: '/admin/buildings/add',
        name: t('add_building'),
        icon: <RiAddLine size={18} />
      }] : []),
      ...(firstBuildingId ? [{
        path: `/admin/buildings/${firstBuildingId}`,
        name: t('building_view') || 'Building View',
        icon: <RiFileList3Line size={18} />
      }] : [])]

    },
    {
      path: '/admin/apartments',
      name: t('apartments'),
      icon: <RiHomeHeartLine size={20} />,
      subItems: [
      {
        path: '/admin/apartments',
        name: t('apartment_list'),
        icon: <RiHomeHeartLine size={18} />
      },
      ...(isPropertyManager ? [{
        path: '/admin/apartments/add',
        name: t('add_apartment'),
        icon: <RiAddLine size={18} />
      }] : [])]

    },
    ...(isPropertyManager ? [{
      path: '/admin/sales',
      name: t('sales'),
      icon: <RiMoneyDollarCircleLine size={20} />,
      subItems: [
      { name: t('sales_management'), path: '/admin/sales', icon: <RiFileList3Line size={18} /> }]

    }] : []),
    {
      path: '/admin/bookings',
      name: t('bookings'),
      icon: <RiCalendarCheckLine size={20} />,
      feature: 'bookings',
      subItems: [
      { name: t('booking_list'), path: '/admin/bookings', icon: <RiFileList3Line size={18} /> },
      { name: t('new_booking'), path: '/admin/bookings/add', icon: <RiAddLine size={18} /> },
      { name: t('booking_calendar'), path: '/admin/bookings/calendar', icon: <RiCalendarEventLine size={18} /> },
      { name: t('waitlist'), path: '/admin/bookings/waitlist', icon: <RiTeamLine size={18} />, feature: 'bookings' },
      { name: t('analytics'), path: '/admin/bookings/analytics', icon: <RiPieChartLine size={18} />, feature: 'analytics' }]

    },
    {
      path: '/admin/inspections',
      name: t('inspections'),
      icon: <RiClipboardLine size={20} />,
      feature: 'inspections',
      subItems: [
      { name: t('all_inspections'), path: '/admin/inspections', icon: <RiFileList2Line size={18} /> }]

    },
    {
      path: '/admin/owners',
      name: t('owners'),
      icon: <RiUserLine size={20} />,
      feature: 'owner_mgmt',
      subItems: [
      { name: t('owner_list'), path: '/admin/owners', icon: <RiFileUserLine size={18} /> },
      ...(isPropertyManager ? [{
        path: '/admin/owners/add',
        name: t('add_owner'),
        icon: <RiUserHeartLine size={18} />
      }] : [])]

    },
    {
      path: '/admin/tenants',
      name: t('tenants'),
      icon: <RiTeamLine size={20} />,
      subItems: [
      { name: t('tenant_list'), path: '/admin/tenants', icon: <RiCommunityLine size={18} /> },
      ...(hasLeasing ? [{
        path: '/admin/tenants/add',
        name: t('add_tenant'),
        icon: <RiAddLine size={18} />
      }] : []),
      ...(hasFinancials ? [{ name: t('overdue_rent'), path: '/admin/tenants/overdue', icon: <RiHistoryLine size={18} /> }] : []),
      ...(isSeniorManagement ? [{ name: t('renewal_risk'), path: '/admin/tenants/renewal-risk', icon: <RiFileTextLine size={18} /> }] : []),
      ...(hasLeasing ? [{ name: t('violations'), path: '/admin/violations', icon: <RiAlarmWarningLine size={18} /> }] : []),
      ...(hasLeasing ? [{ name: t('evictions') || 'Evictions', path: '/admin/evictions', icon: <RiAlertLine size={18} /> }] : []),
      ...(hasScreening ? [{ name: t('screening'), path: '/admin/screening', icon: <RiShieldUserLine size={18} /> }] : [])]

    },
    {
      path: '/admin/leases',
      name: t('leasing_office') || 'Leasing Office',
      icon: <RiFileUserLine size={20} />,
      subItems: [
      { name: t('leases'), path: '/admin/leases', icon: <RiFileTextLine size={18} /> },
      ...(hasLeaseApps ? [{ name: t('lease_applications'), path: '/admin/lease-applications', icon: <RiFileUserLine size={18} /> }] : []),
      ...(hasLeaseApps ? [{ name: t('marketing_links') || 'Marketing Links', path: '/admin/application-links', icon: <RiLinksLine size={18} /> }] : []),
      { name: t('lease_renewals'), path: '/admin/renewals', icon: <RiCalendarCheckLine size={18} /> },
      ...(hasLeaseApps ? [{ name: t('documents'), path: '/admin/documents', icon: <RiFileList3Line size={18} /> }] : []),
      ...(isSeniorManagement ? [{ name: t('bulk_import') || 'Bulk Import', path: '/admin/bulk-import', icon: <RiUpload2Line size={18} /> }] : [])]

    },
    {
      path: '/admin/amenities',
      name: t('amenities_parking'),
      icon: <RiCarLine size={20} />,
      subItems: [
      { name: t('building_amenities'), path: '/admin/amenities', icon: <RiBuildingLine size={18} /> },
      { name: t('parking_spaces'), path: '/admin/amenities#parking', icon: <RiCarLine size={18} /> },
      { name: t('amenity_bookings'), path: '/admin/amenities#bookings', icon: <RiCalendarCheckLine size={18} /> }]

    },
    ...(!isVendor ? [] : [])]

  },

  // Maintenance & Operations Section
  {
    section: 'maintenance_operations',
    items: [
    {
      path: '/admin/maintenance',
      name: t('maintenance'),
      icon: <RiToolsLine size={20} />,
      subItems: [
      { name: t('maintenance_hub'), path: '/admin/maintenance', icon: <RiFileList3Line size={18} /> },
      { name: t('pm_schedules'), path: '/admin/maintenance/schedules', icon: <RiCalendarCheckLine size={18} /> },
      { name: t('work_orders'), path: '/admin/work-orders', icon: <RiToolsLine size={18} /> },
      ...(hasMaterials ? [{ name: t('storage_title'), path: '/admin/storage', icon: <RiArchiveLine size={18} /> }] : []),
      // Only non-vendor roles can access the Vendors management page
      ...(!isVendor && hasMaintenance ? [{ name: t('vendors'), path: '/admin/vendors', icon: <RiUserSettingsLine size={18} /> }] : [])]

    }]

  },

  // Finance Section — Accounting (one section: view the books; record on tenant/owner pages)
  {
    section: 'financial_accounting',
    items: [
    {
      path: '/admin/accounting',
      name: t('accounting') || 'Accounting',
      icon: <RiWallet3Line size={20} />,
      subItems: [
      { name: t('receivables') || 'Receivables', path: '/admin/accounting', icon: <RiMoneyDollarCircleLine size={18} /> },
      ...(hasFinancialData ? [{ name: t('general_ledger'), path: '/admin/gl', icon: <RiFileListLine size={18} /> }] : []),
      ...(hasFinancialData ? [{ name: t('expenses'), path: '/admin/expenses', icon: <RiReceiptLine size={18} /> }] : []),
      ...(hasFinancialData ? [{ name: t('cash_flow_forecast'), path: '/admin/financials/forecast', icon: <RiCalendarLine size={18} /> }] : [])]

    }]

  },

  // Finance Section — Portfolio & KPIs (financial roles only)
  ...(hasFinancialData ? [{
    section: 'financial_portfolio',
    items: [
    {
      path: '/admin/kpi',
      name: t('portfolio_kpis') || 'Portfolio & KPIs',
      icon: <RiBuilding4Line size={20} />,
      subItems: [
      { name: t('kpi_dashboard'), path: '/admin/kpi', icon: <RiBarChartBoxLine size={18} /> },
      { name: t('portfolio'), path: '/admin/portfolio', icon: <RiBuilding4Line size={18} /> }]

    }]

  }] : []),

  // AI Intelligence Section
  ...(!isVendor ? [{
    section: 'ai_intelligence',
    items: [
    {
      path: '/admin/ai',
      name: t('ai_intelligence'),
      icon: <RiBrainLine size={20} />,
      subItems: [
      ...(isSeniorManagement || isLeasingAgent ? [{ name: t('rent_pricing_ai'), path: '/admin/ai-pricing', icon: <RiFlashlightLine size={18} />, feature: 'ai_features' }] : []),
      ...(isSeniorManagement || user?.role === ROLES.ACCOUNTANT ? [{ name: t('churn_prediction'), path: '/admin/churn', icon: <RiBrainLine size={18} />, feature: 'ai_features' }] : []),
      ...(isSeniorManagement || user?.role === ROLES.FRONT_DESK || isLeasingAgent ? [{ name: t('showings'), path: '/admin/showings', icon: <RiCalendarTodoLine size={18} /> }] : []),
      ...(isSeniorManagement || user?.role === ROLES.ACCOUNTANT ? [{ name: t('tenant_surveys'), path: '/admin/surveys', icon: <RiSurveyLine size={18} /> }] : []),
      ...(hasFinancialData ? [{ name: t('insurance'), path: '/admin/insurance', icon: <RiShieldCheckLine size={18} /> }] : [])]

    }]

  }] : []),

  // Reports Section (Admin, Manager, Accountant, Portfolio Manager)
  ...(hasReports ? [{
    section: 'reports_analytics',
    items: [
    {
      path: '/admin/reports',
      name: t('reports'),
      icon: <RiFileChartLine size={20} />,
      feature: 'basic_reports',
      subItems: [
      { name: t('reports_dashboard'), path: '/admin/reports', icon: <RiBarChartBoxLine size={18} />, feature: 'basic_reports' },
      ...(hasFinancialReports ? [
      { name: t('revenue_report'), path: '/admin/reports/revenue', icon: <RiMoneyDollarCircleLine size={18} />, feature: 'analytics' },
      { name: t('ledger_activity_report'), path: '/admin/reports/ledger-activity', icon: <RiFileListLine size={18} />, feature: 'analytics' },
      { name: t('occupancy_report'), path: '/admin/reports/occupancy', icon: <RiBuildingLine size={18} />, feature: 'analytics' },
      { name: t('management_fee_report'), path: '/admin/reports/management-fees', icon: <RiFileChartLine size={18} />, feature: 'analytics' },
      { name: t('reports_1099'), path: '/admin/reports/1099', icon: <RiFileList2Line size={18} />, feature: 'advanced_reports' },
      { name: t('vendor_1099'), path: '/admin/reports/vendor-1099', icon: <RiFileList2Line size={18} />, feature: 'advanced_reports' },
      { name: t('rent_roll'), path: '/admin/reports/rent-roll', icon: <RiFileListLine size={18} />, feature: 'analytics' },
      { name: t('ar_aging'), path: '/admin/reports/ar-aging', icon: <RiFileChartLine size={18} />, feature: 'analytics' }] :
      []),
      { name: t('monthly_payments_report'), path: '/admin/reports/monthly-payments', icon: <RiCalendarLine size={18} />, feature: 'basic_reports' },
      { name: t('payment_logs'), path: '/admin/reports/payment-logs', icon: <RiHistoryLine size={18} />, feature: 'advanced_logs' }]


    }]

  }] : []),
  // Logs Section (Admin Only)
  ...(isAdmin ? [{
    section: 'logs_audit',
    items: [
    {
      path: '/admin/logs',
      name: t('activity_logs'),
      icon: <RiFileHistoryLine size={20} />,
      feature: 'advanced_logs',
      subItems: [
      { name: t('all_activities'), path: '/admin/logs', icon: <RiFileHistoryLine size={18} />, feature: 'advanced_logs' },
      { name: t('tenant_activities'), path: '/admin/logs/tenants', icon: <RiTeamLine size={18} />, feature: 'advanced_logs' },
      { name: t('monthly_revenue'), path: '/admin/logs/monthly-revenue', icon: <RiBarChartBoxLine size={18} />, feature: 'advanced_reports' }]

    }]

  }] : []),

  // System Section
  {
    section: 'system',
    items: [
    ...(isSeniorManagement ? [{
      path: '/admin/notification-log',
      name: t('notification_log') || 'Notification Log',
      icon: <RiBellLine size={20} />,
      subItems: [
      { name: t('notification_log') || 'Notification Log', path: '/admin/notification-log', icon: <RiBellLine size={18} /> }]

    }] : []),
    ...(!isVendor ? [{
      path: '/admin/messages',
      name: t('messages'),
      icon: <RiMessage3Line size={20} />,
      subItems: [
      { name: t('all_messages'), path: '/admin/messages', icon: <RiMessage3Line size={18} /> }]

    }] : []),
    ...(!isVendor ? [{
      path: '/admin/announcements',
      name: t('announcements_manager'),
      icon: <RiMegaphoneLine size={20} />,
      subItems: [
      { name: t('all_announcements'), path: '/admin/announcements', icon: <RiMegaphoneLine size={18} /> },
      { name: t('bulk_comms') || 'Bulk Message', path: '/admin/bulk-comms', icon: <RiSendPlaneLine size={18} /> }]

    }] : []),
    ...(!isVendor ? [{
      path: '/admin/email-templates',
      name: t('email_templates') || 'Email Templates',
      icon: <RiMailLine size={20} />,
      subItems: [
      { name: t('email_templates') || 'Email Templates', path: '/admin/email-templates', icon: <RiMailLine size={18} /> }]

    }] : []),
    {
      path: '/admin/settings',
      name: t('settings'),
      icon: <RiSettings4Line size={20} />,
      subItems: [
      { name: t('profile_settings'), path: '/admin/settings/profile', icon: <RiUserSettingsLine size={18} /> },
      { name: t('subscription'), path: '/admin/settings/subscription', icon: <RiMoneyDollarCircleLine size={18} /> },
      ...(isAdmin ? [{
        path: '/admin/settings/system',
        name: t('system_settings'),
        icon: <RiAdminLine size={18} />
      }] : []),
      ...(isAdmin ? [{
        path: '/admin/contract-templates',
        name: t('contract_templates'),
        icon: <RiFileTextLine size={18} />,
        feature: 'templates'
      }] : []),
      { name: t('payments'), path: '/admin/settings/payments', icon: <RiMoneyDollarCircleLine size={18} /> },
      ...(isPropertyManager ? [{
        path: '/admin/settings/whatsapp',
        name: t('whatsapp_and_sms'),
        icon: <span className="flex items-center gap-0.5"><RiWhatsappLine size={16} /><RiSmartphoneLine size={14} /></span>,
        feature: 'whatsapp'
      }] : []),
      // USA / Canada: Org settings (admin only)
      ...(isAdmin ? [{
        path: '/admin/org-settings',
        name: t('late_fees_org_config'),
        icon: <RiSettingsLine size={18} />,
        feature: 'advanced_reports'
      }] : []),
      ...(isAdmin ? [{
        path: '/admin/settings/webhooks',
        name: t('webhooks'),
        icon: <RiFlashlightLine size={18} />
      }] : [])]

    }]

  }];


  // RTL-aware arrow components
  const ArrowDown = () => <RiArrowDownSLine size={18} />;
  const ArrowRight = () => {
    return isRTL ? <RiArrowLeftSLine size={18} /> : <RiArrowRightSLine size={18} />;
  };

  // Toggle sidebar
  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  // Animation variants
  const sidebarVariants = {
    open: { width: 280, x: 0 },
    closed: { width: isMobile ? 280 : 80, x: isMobile ? isRTL ? '100%' : '-100%' : 0 }
  };

  const textVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: -10 }
  };

  const rtlTextVariants = {
    open: { opacity: 1, x: 0 },
    closed: { opacity: 0, x: 10 }
  };

  const submenuVariants = {
    open: { height: 'auto', opacity: 1 },
    closed: { height: 0, opacity: 0 }
  };

  const submenuItemVariants = {
    open: { x: 0, opacity: 1 },
    closed: { x: isRTL ? 10 : -10, opacity: 0 }
  };

  return (
    <motion.aside
      variants={sidebarVariants}
      animate={isOpen ? 'open' : 'closed'}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className={`h-screen ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-gray-200'} ${
      isRTL ? 'border-l' : 'border-r'} flex flex-col shadow-xl fixed z-50`}
      style={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        [isRTL ? 'right' : 'left']: 0,
        overflowX: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
      
      {/* Sidebar Header */}
      <div className={`flex items-center justify-between h-16 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b px-4`}>
        <div className={`flex items-center ${isOpen ? 'gap-3 overflow-hidden' : 'justify-center w-full'}`}>
          <div className="flex items-center justify-center flex-shrink-0">
            {isOpen ?
            <img
              src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
              alt="QuadCores Logo"
              className="h-8 w-auto"
            /> :

            <img
              src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
              alt="Logo"
              className="h-8 w-8 object-contain"
            />
            }
          </div>
          <AnimatePresence>
            {isOpen && orgName &&
            <motion.span
              variants={isRTL ? rtlTextVariants : textVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className={`text-lg font-bold truncate ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              
                {orgName}
              </motion.span>
            }
          </AnimatePresence>
        </div>

        {/* Toggle Button */}
        <button
          onClick={toggleSidebar}
          className={`p-1.5 rounded-full ${isDarkMode ? 'hover:bg-zinc-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'} ${!isOpen ? 'hidden' : ''}`}
          aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
          
          {isRTL ?
          <RiArrowRightSLine size={20} /> :

          <RiArrowLeftSLine size={20} />
          }
        </button>
      </div>

      {/* Mobile Toggle Button - Visible when sidebar is collapsed */}
      {!isOpen &&
      <button
        onClick={toggleSidebar}
        className={`p-2 mx-auto mt-2 rounded-full ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-zinc-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        aria-label="Expand sidebar">
        
          <RiMenu4Line size={20} />
        </button>
      }

      {/* User Profile */}
      <div className={`p-4 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-b`}>
        <div className={`flex items-center ${isOpen ? '' : 'justify-center'}`}>
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-md">
            <span className="font-bold text-white text-sm">
              {user?.firstName?.charAt(0) || 'U'}
            </span>
          </div>
          <AnimatePresence>
            {isOpen &&
            <motion.div
              variants={isRTL ? rtlTextVariants : textVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="ms-3">
              
                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {user?.firstName} {user?.lastName}
                </p>
                <p className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                  {formatRoleName(user?.role)}
                </p>
              </motion.div>
            }
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-grow overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-zinc-600 scrollbar-track-transparent">
        {menuItems.
        map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            if (isVendor) return item.path === '/admin/dashboard' || item.path === '/admin/maintenance';

            const role = user?.role;
            if (item.path === '/admin/dashboard') return true;
            if (item.path === '/admin/users') return isAdminRole(role);
            if (item.path === '/admin/buildings') return canManageProperties(role) && role !== ROLES.ACCOUNTANT;
            if (item.path === '/admin/apartments') return canManageProperties(role) && role !== ROLES.ACCOUNTANT;
            if (item.path === '/admin/bookings') return canManageLeases(role) || canManageMaintenance(role);
            if (item.path === '/admin/inspections') return canManageLeases(role) || canManageMaintenance(role);
            if (item.path === '/admin/tenants') return canOperateSystem(role);
            if (item.path === '/admin/owners') return canManageOwners(role);
            if (item.path === '/admin/leases') return canManageLeases(role) || canManageLeaseApplications(role);
            if (item.path === '/admin/maintenance') return canManageMaintenance(role);
            if (item.path === '/admin/payments') return canManagePayments(role);
            if (item.path === '/admin/expenses') return canManageFinancialData(role);
            if (item.path === '/admin/storage') return canManageMaterials(role);
            if (item.path === '/admin/reports') return canViewBasicReports(role);
            if (item.path === '/admin/logs') return isAdminRole(role);
            if (item.path === '/admin/settings') return true; // Profile is always accessible
            if (item.path === '/admin/sales') return isManagement(role);
            if (item.path === '/admin/amenities') return canManageMaintenance(role);
            if (item.path === '/admin/announcements') return canManageOwners(role);
            if (item.path === '/admin/email-templates') return isManagement(role);
            if (item.path === '/admin/notification-log') return isManagement(role);
            if (item.path === '/admin/vendors') return canManageOwners(role);

            return true;
          })

        })).
        filter((section) => section.items.length > 0).
        map((section, idx) =>
        <div key={`section-${idx}`} className="mt-4 px-4">
            {/* Section Title */}
            <AnimatePresence>
              {isOpen &&
            <motion.div
              variants={isRTL ? rtlTextVariants : textVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className={`mb-2 text-xs font-semibold ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} uppercase tracking-wider ${isRTL ? 'text-right' : 'text-left'}`}>
              
                  {t(section.section)}
                </motion.div>
            }
            </AnimatePresence>

            {/* Menu Items */}
            <ul className="space-y-1">
              {section.items.map((item) => {return (
                <li key={item.path}>
                  {item.subItems ?
                  <div>
                      {/* Parent Menu Item with Submenu */}
                      <button
                      onClick={() => isItemLocked(item) ? navigate('/admin/settings/subscription') : toggleSubmenu(item.path)}
                      className={`flex items-center justify-between w-full py-2.5 px-3 rounded-lg transition-all ${isItemLocked(item) ?
                      isDarkMode ? 'text-amber-500/60 hover:text-amber-400 hover:bg-zinc-800' : 'text-amber-600/60 hover:text-amber-600 hover:bg-amber-50/50' :
                      isRouteActive(item.path) ?
                      isDarkMode ?
                      'bg-zinc-800 text-white font-medium' :
                      'bg-zinc-50 text-red-600 font-medium' :
                      isDarkMode ?
                      'text-zinc-400 hover:text-white hover:bg-zinc-800' :
                      'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
                      }>
                      
                        <div className="flex items-center">
                          <span className={`flex items-center justify-center w-6 h-6 ${isItemLocked(item) ? 'text-amber-500/70' : isRouteActive(item.path) ? isDarkMode ? 'text-red-400' : 'text-red-600' : ''}`}>
                            {isItemLocked(item) ? <RiLockLine size={20} /> : item.icon}
                          </span>
                          <AnimatePresence>
                            {isOpen &&
                          <motion.span
                            variants={isRTL ? rtlTextVariants : textVariants}
                            initial="closed"
                            animate="open"
                            exit="closed"
                            className="ms-3 text-sm flex items-center gap-2">
                            
                                {item.name}
                                {isItemLocked(item) &&
                            <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/30">
                                    {t("pro")}
                                  </span>
                            }
                              </motion.span>
                          }
                          </AnimatePresence>
                        </div>
                        <AnimatePresence>
                          {isOpen &&
                        <motion.span
                          variants={textVariants}
                          initial="closed"
                          animate="open"
                          exit="closed"
                          className={`transition-transform duration-300 ${expandedMenus[item.path] ? 'rotate-180' : ''}`}>
                          
                              {expandedMenus[item.path] ?
                          <ArrowDown /> :

                          <ArrowRight />
                          }
                            </motion.span>
                        }
                        </AnimatePresence>
                      </button>

                      {/* Submenu Items */}
                      <AnimatePresence>
                        {isOpen && expandedMenus[item.path] &&
                      <motion.ul
                        variants={submenuVariants}
                        initial="closed"
                        animate="open"
                        exit="closed"
                        transition={{ duration: 0.2 }}
                        className="ms-8 mt-1 space-y-1 overflow-hidden">
                        
                            {item.subItems.map((subItem, subIdx) => {
                          const locked = isItemLocked(subItem);
                          return (
                            <motion.li
                              key={subItem.path}
                              variants={submenuItemVariants}
                              initial="closed"
                              animate="open"
                              exit="closed"
                              transition={{ delay: 0.05 * subIdx }}
                              className={isRTL ? "origin-right" : "origin-left"}>
                              
                                <NavLink
                                to={locked ? '/pricing' : subItem.path}
                                onClick={() => isMobile && setIsOpen(false)}
                                className={({ isActive }) => `flex items-center py-2 px-3 rounded-md text-sm transition-colors ${locked ?
                                isDarkMode ? 'text-amber-500/60 hover:text-amber-400 hover:bg-zinc-800/50' : 'text-amber-600/60 hover:text-amber-600 hover:bg-amber-50/50' :
                                isActive ?
                                isDarkMode ?
                                'bg-red-900/20 text-red-400 font-medium' :
                                'bg-zinc-50 text-red-600 font-medium' :
                                isDarkMode ?
                                'text-zinc-400 hover:text-white hover:bg-zinc-800/50' :
                                'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
                                }>
                                
                                  <span className="flex items-center justify-center w-5 h-5">
                                    {locked ? <RiLockLine size={16} className="text-amber-500/70" /> : subItem.icon}
                                  </span>
                                  <span className={`ms-2 text-sm text-start flex-1`}>
                                    {subItem.name}
                                  </span>
                                  {locked &&
                                <span className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/30 ms-1 shrink-0">
                                      {t("pro_1")}
                                    </span>
                                }
                                </NavLink>
                              </motion.li>);

                        })}
                          </motion.ul>
                      }
                      </AnimatePresence>
                    </div> : (

                  /* Single Menu Item (no submenu) */
                  <NavLink
                    to={item.path}
                    end={item.exact}
                    onClick={() => isMobile && setIsOpen(false)}
                    className={({ isActive }) => `flex items-center py-2.5 px-3 rounded-lg transition-colors ${isActive ?
                    isDarkMode ?
                    'bg-red-600 text-white font-medium shadow-md shadow-red-900/20' :
                    'bg-red-600 text-white font-medium shadow-md shadow-red-500/20' :
                    isDarkMode ?
                    'text-zinc-400 hover:text-white hover:bg-zinc-800/70' :
                    'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`
                    }>
                    
                      <span className="flex items-center justify-center w-6 h-6">
                        {item.icon}
                      </span>
                      <AnimatePresence>
                        {isOpen &&
                      <motion.span
                        variants={isRTL ? rtlTextVariants : textVariants}
                        initial="closed"
                        animate="open"
                        exit="closed"
                        className="ms-3 text-sm text-start">
                        
                            {item.name}
                          </motion.span>
                      }
                      </AnimatePresence>
                    </NavLink>)
                  }
                </li>);}
            )}
            </ul>
          </div>
        )}
      </nav>

      {/* Logout Button */}
      <div className={`p-4 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'} border-t mt-auto`}>
        <button
          onClick={handleLogout}
          className={`flex items-center w-full py-2.5 px-3 rounded-lg transition-colors ${isDarkMode ?
          'text-zinc-400 hover:text-white hover:bg-red-900/20' :
          'text-gray-600 hover:text-red-600 hover:bg-zinc-50'}`
          }>
          
          <span className="flex items-center justify-center w-6 h-6">
            <RiLogoutBoxLine size={20} />
          </span>
          <AnimatePresence>
            {isOpen &&
            <motion.span
              variants={isRTL ? rtlTextVariants : textVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="ms-3 text-sm text-start">
              
                {t('logout')}
              </motion.span>
            }
          </AnimatePresence>
        </button>
      </div>
    </motion.aside>);

};

export default Sidebar;